import type { ReactNode } from 'react';
import { Icon, type IconName } from './Icon';

export function DemoBadge({ label = 'Demo' }: { label?: string }) {
  return <span className="badge badge-demo" title="Dado demonstrativo e fictício. Nenhuma conta está conectada.">{label}</span>;
}

export function PageHead({ title, lead, actions }: { title: string; lead?: ReactNode; actions?: ReactNode }) {
  return (
    <header className="page-head">
      <div><h1>{title}</h1>{lead && <p>{lead}</p>}</div>
      {actions && <div className="chips">{actions}</div>}
    </header>
  );
}

export interface StateAction { label: string; href?: string; icon?: IconName; primary?: boolean; onClick?: () => void }

export interface EmptyProps { icon?: IconName; title: string; text?: ReactNode; actions?: StateAction[]; compact?: boolean }

function ActionButtons({ actions }: { actions: StateAction[] }) {
  return (
    <div className="actions">
      {actions.map((a) => a.href
        ? <a key={a.label} className={`btn ${a.primary ? 'btn-primary' : ''}`} href={a.href}>{a.icon && <Icon name={a.icon} />}{a.label}</a>
        : <button key={a.label} type="button" className={`btn ${a.primary ? 'btn-primary' : ''}`} onClick={a.onClick}>{a.icon && <Icon name={a.icon} />}{a.label}</button>)}
    </div>
  );
}

export function EmptyState({ icon = 'inbox', title, text, actions, compact }: EmptyProps) {
  return (
    <div className={`state ${compact ? 'compact' : ''}`}>
      <div className="state-icon"><Icon name={icon} /></div>
      <h2>{title}</h2>
      {text && <p>{text}</p>}
      {actions && actions.length > 0 && <ActionButtons actions={actions} />}
    </div>
  );
}

export function ErrorState({ error, onRetry, compact }: { error: Error & { code?: string; requestId?: string }; onRetry: () => void; compact?: boolean }) {
  return (
    <div className={`state error ${compact ? 'compact' : ''}`} role="alert">
      <div className="state-icon"><Icon name="alert" /></div>
      <h2>Não foi possível carregar</h2>
      <p>{error.message || 'Ocorreu um erro inesperado.'}</p>
      {error.code && <span className="detail">código: {error.code}{error.requestId ? ` · requisição ${error.requestId.slice(0, 8)}` : ''}</span>}
      <div className="actions"><button type="button" className="btn btn-primary" onClick={onRetry}><Icon name="refresh" />Tentar novamente</button></div>
    </div>
  );
}

export type SkeletonKind = 'list' | 'cards' | 'block' | 'line';

export function Skeleton({ kind = 'list', count }: { kind?: SkeletonKind; count?: number }) {
  const n = count ?? (kind === 'cards' ? 3 : 4);
  const range = Array.from({ length: n }, (_, i) => i);
  let body: ReactNode;
  if (kind === 'cards') {
    body = <div className="projects">{range.map((i) => <div key={i} className="sk-card"><div className="sk h" /><div className="sk w80" /><div className="sk w60" /><div className="sk sk-block" /></div>)}</div>;
  } else if (kind === 'block') {
    body = <div className="sk-card"><div className="sk h" /><div className="sk sk-block" /><div className="sk w60" /></div>;
  } else if (kind === 'line') {
    body = <div className="skeleton"><div className="sk w80" /><div className="sk w60" /><div className="sk w40" /></div>;
  } else {
    body = <div className="sk-card">{range.map((i) => <div key={i} className="skeleton"><div className={`sk ${i % 2 ? 'w60' : 'w80'}`} /><div className="sk w40" /></div>)}</div>;
  }
  return <><div aria-hidden="true">{body}</div><span className="sr-only">Carregando…</span></>;
}

export function LockedNote({ children }: { children: ReactNode }) {
  return <div className="notice lock"><Icon name="lock" /><span>{children}</span></div>;
}

export function Notice({ kind, icon, children }: { kind?: 'warn'; icon: IconName; children: ReactNode }) {
  return <div className={`notice ${kind ?? ''}`}><Icon name={icon} /><span>{children}</span></div>;
}

/* Leitura → Preparação → Execução. A execução fica sempre trancada nesta etapa. */
export function Pipeline({ read = true, prepare = false }: { read?: boolean; prepare?: boolean }) {
  const step = (on: boolean, label: string, icon: IconName, locked = false) => (
    <span className={`pipe-step ${on ? 'done' : ''} ${locked ? 'locked' : ''}`}>
      <Icon name={locked ? 'lock' : on ? 'check' : icon} />{label}
    </span>
  );
  return (
    <div className="pipeline" aria-label="Etapas da ação">
      {step(read, 'Leitura', 'eye')}<span className="pipe-link" />
      {step(prepare, 'Preparação', 'edit')}<span className="pipe-link" />
      {step(false, 'Execução · requer sua confirmação', 'send', true)}
    </div>
  );
}

export function Progress({ value, tone }: { value: number; tone?: 'signal' }) {
  const v = Math.max(0, Math.min(100, value));
  return <div className={`progress ${tone ?? ''}`} role="progressbar" aria-valuenow={Math.round(v)} aria-valuemin={0} aria-valuemax={100}><span style={{ width: `${v}%` }} /></div>;
}

export function CardHead({ icon, title, extra }: { icon: IconName; title: string; extra?: ReactNode }) {
  return <div className="card-head"><Icon name={icon} /><h2>{title}</h2><span className="spacer" />{extra}</div>;
}
